import type { IdentityCallbackData } from 'branch-sdk';

import { config } from 'src/config';
import { initializeBraze } from 'src/shared/telemetry';

export interface IdentifyParameters {
  email: string;
  userId?: number;
}

export interface SegmentIdentifyTraits {
  account_type?: 'pro' | 'homeowner';
}

export interface SegmentIdentifyParamaters
  extends IdentifyParameters,
    SegmentIdentifyTraits {}

export const TIMEOUT_MILLISECONDS = 3 * 1000;
export const TIMEOUT_MESSAGE = 'timeout';

const segmentIdentify = (
  params: SegmentIdentifyTraits | SegmentIdentifyParamaters,
): Promise<void> => {
  if (!config.SEGMENT_API_KEY) {
    return Promise.resolve();
  }

  if ('userId' in params && params.userId) {
    const { userId, ...traits } = params;

    return new Promise(resolve =>
      // Hyperion's user ID is a number
      // Segment's API dictates that ID needs to be a string
      // See DS-846
      // eslint-disable-next-line no-promise-executor-return
      window.analytics.identify(userId.toString(), traits, resolve),
    );
  }

  // See DS-846
  // eslint-disable-next-line no-promise-executor-return
  return new Promise(resolve => window.analytics.identify(params, resolve));
};

const branchIdentify = ({
  email,
}: IdentifyParameters): Promise<IdentityCallbackData | null> => {
  if (!config.BRANCH_API_KEY) {
    return Promise.resolve(null);
  }

  return new Promise((resolve, reject) => {
    window.branch.setIdentity(email, (err, data) =>
      err ? reject(err) : resolve(data),
    );
  });
};

const brazeIdentify = async ({ userId, email }: IdentifyParameters) => {
  if (!config.BRANCH_API_KEY) return;

  const braze = await initializeBraze();

  if (!braze || !userId) return;

  braze.changeUser(userId.toString());
  braze.getUser().setEmail(email);
  braze.display.automaticallyShowNewInAppMessages();
  braze.openSession();
};

const identify = async (
  parameters: IdentifyParameters,
): Promise<IdentityCallbackData | null> => {
  const identifyAll = Promise.all([
    segmentIdentify(parameters),
    branchIdentify(parameters),
    brazeIdentify(parameters),
  ]);

  const timeout = new Promise<void>((_, reject) => {
    setTimeout(() => reject(new Error(TIMEOUT_MESSAGE)), TIMEOUT_MILLISECONDS);
  });

  await Promise.race([identifyAll, timeout]);

  return null;
};

export { identify, segmentIdentify };
